import React, { useState, useMemo } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Button,
  HStack,
  Text,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Input,
  Select,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { FiMoreVertical, FiEdit, FiTrash2, FiCalendar, FiFilter, FiX } from "react-icons/fi";
import { format } from "date-fns";

const HolidaysTable = ({
  holidaysData = [],
  handleEdit,
  handleDelete,
  getHolidaysIsLoading = false,
  refetchGetHolidays,
  filters = {},
  onFilterChange,
  onClearFilters,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [localFilters, setLocalFilters] = useState(filters);
  const toast = useToast();

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return holidaysData;

    return [...holidaysData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === "date") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [holidaysData, sortConfig]);

  // Filter data
  const filteredData = useMemo(() => {
    return sortedData.filter((holiday) => {
      const matchesYear = !localFilters.year || 
        new Date(holiday.date).getFullYear().toString() === localFilters.year;
      
      const matchesMonth = !localFilters.month || 
        (new Date(holiday.date).getMonth() + 1).toString() === localFilters.month;
      
      const matchesRecurring = localFilters.is_recurring === undefined || 
        holiday.is_recurring.toString() === localFilters.is_recurring;
      
      const matchesSearch = !localFilters.search || 
        holiday.name.toLowerCase().includes(localFilters.search.toLowerCase()) ||
        holiday.description?.toLowerCase().includes(localFilters.search.toLowerCase());

      return matchesYear && matchesMonth && matchesRecurring && matchesSearch;
    });
  }, [sortedData, localFilters]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const handleClearFilters = () => {
    setLocalFilters({});
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    try {
      await handleDelete(holiday.holiday_id);
      toast({
        title: "Holiday deleted",
        description: `${holiday.name} has been deleted successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete holiday",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  const getMonthOptions = () => {
    return [
      { value: "1", label: "January" },
      { value: "2", label: "February" },
      { value: "3", label: "March" },
      { value: "4", label: "April" },
      { value: "5", label: "May" },
      { value: "6", label: "June" },
      { value: "7", label: "July" },
      { value: "8", label: "August" },
      { value: "9", label: "September" },
      { value: "10", label: "October" },
      { value: "11", label: "November" },
      { value: "12", label: "December" },
    ];
  };

  if (getHolidaysIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color="gray.600">Loading holidays...</Text>
      </Box>
    );
  }

  if (!holidaysData || holidaysData.length === 0) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>No holidays found</AlertTitle>
          <AlertDescription>
            {Object.keys(localFilters).length > 0 
              ? "Try adjusting your filters or add a new holiday."
              : "Get started by adding your first holiday."
            }
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Filters */}
      <Box
        bg="gray.50"
        p={4}
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <Flex direction={{ base: "column", md: "row" }} gap={4} align="center">
          <Input
            placeholder="Search holidays..."
            value={localFilters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            maxW="300px"
          />
          
          <Select
            placeholder="Year"
            value={localFilters.year || ""}
            onChange={(e) => handleFilterChange("year", e.target.value)}
            maxW="120px"
          >
            {getYearOptions().map((year) => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </Select>
          
          <Select
            placeholder="Month"
            value={localFilters.month || ""}
            onChange={(e) => handleFilterChange("month", e.target.value)}
            maxW="140px"
          >
            {getMonthOptions().map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </Select>
          
          <Select
            placeholder="Recurring"
            value={localFilters.is_recurring || ""}
            onChange={(e) => handleFilterChange("is_recurring", e.target.value)}
            maxW="120px"
          >
            <option value="true">Recurring</option>
            <option value="false">One-time</option>
          </Select>
          
          <Button
            leftIcon={<FiX />}
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
            isDisabled={Object.keys(localFilters).length === 0}
          >
            Clear
          </Button>
        </Flex>
      </Box>

      {/* Table */}
      <Box
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th 
                cursor="pointer" 
                onClick={() => handleSort("name")}
                _hover={{ bg: "gray.100" }}
              >
                <HStack spacing={2}>
                  <Text>Holiday Name</Text>
                  {getSortIcon("name")}
                </HStack>
              </Th>
              <Th 
                cursor="pointer" 
                onClick={() => handleSort("date")}
                _hover={{ bg: "gray.100" }}
              >
                <HStack spacing={2}>
                  <FiCalendar />
                  <Text>Date</Text>
                  {getSortIcon("date")}
                </HStack>
              </Th>
              <Th>Description</Th>
              <Th>Type</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map((holiday) => (
              <Tr key={holiday.holiday_id} _hover={{ bg: "gray.50" }}>
                <Td fontWeight="500">{holiday.name}</Td>
                <Td>
                  <HStack spacing={2}>
                    <FiCalendar color="gray.500" />
                    <Text>{formatDate(holiday.date)}</Text>
                  </HStack>
                </Td>
                <Td>
                  <Text color="gray.600" noOfLines={2}>
                    {holiday.description || "No description"}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme={holiday.is_recurring ? "green" : "blue"}
                    variant="subtle"
                    borderRadius="full"
                    px={3}
                  >
                    {holiday.is_recurring ? "Recurring" : "One-time"}
                  </Badge>
                </Td>
                <Td>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<FiMoreVertical />}
                      variant="ghost"
                      size="sm"
                      aria-label="Actions"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<FiEdit />}
                        onClick={() => handleEdit(holiday)}
                      >
                        Edit Holiday
                      </MenuItem>
                      <MenuItem
                        icon={<FiTrash2 />}
                        onClick={() => handleDeleteHoliday(holiday)}
                        color="red.500"
                      >
                        Delete Holiday
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Summary */}
      <Box
        bg="blue.50"
        p={3}
        borderRadius="md"
        border="1px solid"
        borderColor="blue.200"
      >
        <Text fontSize="sm" color="blue.700">
          Showing {filteredData.length} of {holidaysData.length} holidays
          {Object.keys(localFilters).length > 0 && " (filtered)"}
        </Text>
      </Box>
    </VStack>
  );
};

export default HolidaysTable; 